import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Headphones,
  Building2,
  SlidersHorizontal,
  Package,
  Monitor,
  Users,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Plus,
  Trash2,
  RotateCcw,
  Lock,
  CheckCircle2,
  Check,
  ChevronDown
} from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import {
  validateUKPostcode,
  formatUKPostcode,
  validateUKTelephone,
  normalizeUKTelephone,
} from "@/utils/ukValidation";

const WHOLESALER_OPTIONS = [
  "AAH Pharmaceuticals",
  "Alliance Healthcare",
  "Phoenix Healthcare Distribution",
  "Ethigen",
  "OTC Direct",
  "Trident",
  "B&S Distribution",
  "Lexon",
  "Sigma Pharmaceuticals",
  "Mawdsleys",
  "DE Pharmaceuticals",
];

export default function RequestAccess() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1: Pharmacy Details
  const [step1, setStep1] = useState({
    companyName: "",
    pharmacyName: "",
    pharmacyAddress: "",
    pharmacyPostcode: "",
    telephoneNumber: "",
    emailAddress: "",
    contactName: "",
    setupType: "Preferred Supplier", // "Price Comparison" | "Preferred Supplier"
    transmissionType: "PMR to Orderpad", // "Orderpad" | "PMR to Orderpad" | "PMR to Order History"
    pmrSystem: "Proscript", // "Proscript" | "Apotec" | "Rx Web" | "Positive Solutions Analyst"
    partOfGroup: "No", // "Yes" | "No"
    groupNumber: "",
    groupName: "",
    groupAccounts: "",
    buyingGroup: "",
  });

  // Step 2: Ordering Preferences
  const [genericsWholesalers, setGenericsWholesalers] = useState([""]);
  const [piWholesalers, setPiWholesalers] = useState([""]);

  // Step 3: Supplier Accounts & Declaration
  const [wholesalerAccounts, setWholesalerAccounts] = useState([
    { supplier: "", accountNumber: "" },
  ]);
  const [declarationAgreed, setDeclarationAgreed] = useState(false);
  const todayStr = new Date().toISOString().split("T")[0];

  const [declarationDate, setDeclarationDate] = useState(todayStr);
  const [repName, setRepName] = useState("");
  const [repPosition, setRepPosition] = useState("");
  const [signatureData, setSignatureData] = useState(null);

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Scroll to top when changing steps
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const FIELD_MAX_LENGTHS = {
    companyName: 50,
    pharmacyName: 50,
    contactName: 50,
    pharmacyAddress: 150,
    pharmacyPostcode: 10,
    telephoneNumber: 20,
    emailAddress: 100,
    groupNumber: 10,
    groupName: 50,
    groupAccounts: 50,
    buyingGroup: 50,
  };

  const validateStep1 = (currentStep1 = step1) => {
    const e = {};
    if (!currentStep1.companyName.trim()) e.companyName = "Required";
    if (!currentStep1.pharmacyName.trim()) e.pharmacyName = "Required";
    if (!currentStep1.pharmacyAddress.trim()) e.pharmacyAddress = "Required";

    // UK Postcode Validation
    if (!currentStep1.pharmacyPostcode.trim()) {
      e.pharmacyPostcode = "Required";
    } else if (!validateUKPostcode(currentStep1.pharmacyPostcode)) {
      e.pharmacyPostcode = "Please enter a valid UK postcode.";
    }

    // UK Telephone Number Validation
    if (!currentStep1.telephoneNumber.trim()) {
      e.telephoneNumber = "Required";
    } else if (!validateUKTelephone(currentStep1.telephoneNumber)) {
      e.telephoneNumber = "Please enter a valid UK telephone number.";
    }

    // Email Address Validation
    if (!currentStep1.emailAddress.trim()) {
      e.emailAddress = "Required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentStep1.emailAddress)) {
      e.emailAddress = "Invalid email";
    }

    if (!currentStep1.contactName.trim()) e.contactName = "Required";
    return e;
  };

  const handleStep1Change = (key, val) => {
    const maxLen = FIELD_MAX_LENGTHS[key];
    let finalVal = val;
    let maxError = undefined;

    if (maxLen && val.length >= maxLen) {
      finalVal = val.slice(0, maxLen);
      maxError = "Maximum length exceeded.";
    }

    const updatedStep1 = { ...step1, [key]: finalVal };
    setStep1(updatedStep1);

    // Dynamic validation on change if touched
    if (touched[key]) {
      const fullErrors = validateStep1(updatedStep1);
      setErrors((prev) => {
        const copy = { ...prev };
        if (maxError) {
          copy[key] = maxError;
        } else if (fullErrors[key]) {
          copy[key] = fullErrors[key];
        } else {
          delete copy[key];
        }
        return copy;
      });
    }
  };

  const handleStep1Blur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));

    let finalVal = step1[key];
    if (key === "pharmacyPostcode" && finalVal.trim()) {
      finalVal = formatUKPostcode(finalVal);
    } else if (key === "telephoneNumber" && finalVal.trim()) {
      finalVal = normalizeUKTelephone(finalVal);
    }

    const updatedStep1 = { ...step1, [key]: finalVal };
    if (finalVal !== step1[key]) {
      setStep1(updatedStep1);
    }

    const fullErrors = validateStep1(updatedStep1);
    setErrors((prev) => {
      const copy = { ...prev };
      if (fullErrors[key]) {
        copy[key] = fullErrors[key];
      } else {
        delete copy[key];
      }
      return copy;
    });
  };

  const validateStep2 = () => {
    const e = {};
    const hasEmptyGenerics = genericsWholesalers.some((w) => !w || !w.trim());
    if (genericsWholesalers.length === 0 || hasEmptyGenerics) {
      e.generics = "Please select a wholesaler for all priority slots";
    }
    const hasEmptyPi = piWholesalers.some((w) => !w || !w.trim());
    if (piWholesalers.length === 0 || hasEmptyPi) {
      e.pi = "Please select a wholesaler for all priority slots";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e = { ...errors };

    wholesalerAccounts.forEach((acc, idx) => {
      const supplierKey = `acc_supplier_${idx}`;
      const numberKey = `acc_number_${idx}`;

      if (!acc.supplier || !acc.supplier.trim()) {
        e[supplierKey] = "Required";
      } else if (e[supplierKey] === "Required") {
        delete e[supplierKey];
      }

      if (!acc.accountNumber || !acc.accountNumber.trim()) {
        e[numberKey] = "Required";
      } else if (acc.accountNumber.length > 12) {
        e[numberKey] = "Maximum length exceeded.";
      } else if (e[numberKey] === "Required") {
        delete e[numberKey];
      }
    });

    if (!declarationAgreed) {
      e.declaration = "You must agree to the declaration before submitting.";
    } else if (e.declaration) {
      delete e.declaration;
    }

    if (!repName.trim()) {
      e.repName = "Required";
    } else if (repName.length >= 50) {
      e.repName = "Maximum length exceeded.";
    }

    if (!repPosition.trim()) {
      e.repPosition = "Required";
    } else if (repPosition.length >= 50) {
      e.repPosition = "Maximum length exceeded.";
    }

    if (!declarationDate.trim()) {
      e.declarationDate = "Required";
    } else if (e.declarationDate) {
      delete e.declarationDate;
    }

    if (!signatureData) {
      e.signature = "Please sign in the box above.";
    } else if (e.signature) {
      delete e.signature;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isPriceComparison = step1.setupType === "Price Comparison";

  const handleNext = () => {
    if (step === 1) {
      // Mark all Step 1 fields as touched
      const allTouched = {
        companyName: true,
        pharmacyName: true,
        pharmacyAddress: true,
        pharmacyPostcode: true,
        telephoneNumber: true,
        emailAddress: true,
        contactName: true,
      };
      setTouched((prev) => ({ ...prev, ...allTouched }));

      let formattedPostcode = step1.pharmacyPostcode;
      if (step1.pharmacyPostcode.trim()) {
        formattedPostcode = formatUKPostcode(step1.pharmacyPostcode);
      }
      let normalizedPhone = step1.telephoneNumber;
      if (step1.telephoneNumber.trim()) {
        normalizedPhone = normalizeUKTelephone(step1.telephoneNumber);
      }

      const updatedStep1 = {
        ...step1,
        pharmacyPostcode: formattedPostcode,
        telephoneNumber: normalizedPhone,
      };
      setStep1(updatedStep1);

      const fullErrors = validateStep1(updatedStep1);
      setErrors(fullErrors);

      if (Object.keys(fullErrors).length === 0) {
        if (isPriceComparison) {
          setStep(3);
        } else {
          setStep(2);
        }
      }
    } else if (step === 2) {
      if (validateStep2()) setStep(3);
    }
  };

  const handlePrev = () => {
    if (step === 3) {
      if (isPriceComparison) {
        setStep(1);
      } else {
        setStep(2);
      }
    } else if (step === 2) {
      setStep(1);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateStep3()) return;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("company_name", step1.companyName);
      formData.append("pharmacy_name", step1.pharmacyName);
      formData.append("pharmacy_address", step1.pharmacyAddress);
      formData.append("pharmacy_postcode", step1.pharmacyPostcode);
      formData.append("address", `${step1.pharmacyAddress}, ${step1.pharmacyPostcode}`);
      formData.append("contact_phone", step1.telephoneNumber);
      formData.append("user_email_reg", step1.emailAddress);
      formData.append("full_name", step1.contactName);
      formData.append("pmr_system", step1.pmrSystem);
      formData.append("setup_type", step1.setupType);
      formData.append("transmission_type", step1.transmissionType);
      formData.append("part_of_group", step1.partOfGroup);
      if (step1.partOfGroup === "Yes") {
        formData.append("group_number", step1.groupNumber);
        formData.append("group_name", step1.groupName);
        formData.append("group_accounts", step1.groupAccounts);
        formData.append("buying_group", step1.buyingGroup);
      }
      formData.append("generics_wholesalers", JSON.stringify(genericsWholesalers));
      formData.append("pi_wholesalers", JSON.stringify(piWholesalers));
      formData.append("wholesaler_accounts", JSON.stringify(wholesalerAccounts));
      formData.append("declaration_agreed", "Yes");
      formData.append("declaration_date", declarationDate);
      formData.append("rep_name", repName);
      formData.append("rep_position", repPosition);
      formData.append("signature", signatureData || "");

      const response = await fetch("/assets/Contact/request_free_access.php", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setStep(4);
      } else {
        console.warn("API response code:", response.status);
        setStep(4);
      }
    } catch (err) {
      console.error("Submission failed:", err);
      setStep(4);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4FAF7] bg-[radial-gradient(#d1fae5_1px,transparent_1px)] [background-size:24px_24px] text-slate-800 pb-20 pt-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {step === 4 ? (
          /* STEP 4: CONFIRMATION SCREEN */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="max-w-xl mx-auto my-12 bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-slate-100 text-center relative overflow-hidden"
          >
            <div className="h-16 w-16 mx-auto rounded-full bg-[#D7F4E6] text-[#00966B] flex items-center justify-center mb-6">
              <CheckCircle2 className="h-9 w-9 text-[#00966B]" />
            </div>

            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#00966B] mb-2">
              REQUEST RECEIVED
            </div>

            <h1 data-testid={CONTACT.successMessage} className="text-3xl font-extrabold text-[#0A2540] mb-4">
              Your request has been submitted
            </h1>

            <p className="text-slate-600 leading-relaxed mb-8 max-w-md mx-auto text-sm sm:text-base">
              Thank you for requesting access to Cascade Plus. Our team will review your information and contact you regarding your setup.
            </p>

            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#0F365C] transition-all shadow-md hover:shadow-lg"
            >
              <ArrowLeft className="h-4 w-4" /> Return to Home
            </Link>

            <div className="my-8 border-t border-slate-100" />

            <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
              <Headphones className="h-4 w-4 text-[#00966B]" />
              <span>Need help?</span>
              <a href="mailto:info@cascadeplus.co.uk" className="text-[#00966B] font-medium hover:underline">
                info@cascadeplus.co.uk
              </a>
            </div>
          </motion.div>
        ) : (
          /* STEPS 1-3 MULTI-STEP LAYOUT */
          <div className="grid lg:grid-cols-12 gap-4 items-start">

            {/* LEFT SIDEBAR */}
            <div className="lg:col-span-2 space-y-6 mt-20">
              <div className="bg-transparent space-y-6">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00966B] block mb-1">
                    GET STARTED
                  </span>
                  <h1 data-testid={CONTACT.pageTitle} className="text-2xl font-extrabold text-[#0A2540] tracking-tight leading-snug">
                    Cascade Plus Set-up Form
                  </h1>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    Complete the details below so we can configure Cascade Plus for your pharmacy.
                  </p>
                </div>

                {/* STEP NAVIGATOR */}
                <div className="space-y-6 relative before:absolute before:left-4 before:top-4 before:bottom-4 before:w-0.5 before:bg-slate-200">
                  {/* Step 1 */}
                  <div
                    onClick={() => step > 1 && setStep(1)}
                    className={`relative flex items-center gap-4 cursor-pointer group ${step === 1 ? "opacity-100" : "opacity-75 hover:opacity-100"}`}
                  >
                    <div
                      className={`relative z-10 h-8 w-8 shrink-0 aspect-square rounded-full flex items-center justify-center font-bold text-xs transition-all ${step === 1
                        ? "bg-[#00966B] text-white ring-4 ring-emerald-100"
                        : step > 1
                          ? "bg-[#00966B] text-white"
                          : "bg-slate-300 text-slate-600"
                        }`}
                    >
                      {step > 1 ? <Check className="h-4 w-4" /> : "1"}
                    </div>
                    <div>
                      <span className={`text-xs font-bold block ${step === 1 ? "text-[#00966B]" : "text-slate-700"}`}>
                        Pharmacy Details
                      </span>
                    </div>
                  </div>

                  {/* Step 2: Ordering Preferences (Only for Preferred Supplier) */}
                  {!isPriceComparison && (
                    <div
                      onClick={() => step > 2 && setStep(2)}
                      className={`relative flex items-center gap-4 cursor-pointer group ${step === 2 ? "opacity-100" : "opacity-75 hover:opacity-100"}`}
                    >
                      <div
                        className={`relative z-10 h-8 w-8 shrink-0 aspect-square rounded-full flex items-center justify-center font-bold text-xs transition-all ${step === 2
                          ? "bg-[#00966B] text-white ring-4 ring-emerald-100"
                          : step > 2
                            ? "bg-[#00966B] text-white"
                            : "bg-slate-300 text-slate-600"
                          }`}
                      >
                        {step > 2 ? <Check className="h-4 w-4" /> : "2"}
                      </div>
                      <div>
                        <span className={`text-xs font-bold block ${step === 2 ? "text-[#00966B]" : "text-slate-500"}`}>
                          Ordering Preferences
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Final Step: Supplier Accounts & Declaration */}
                  <div
                    onClick={() => step === 1 && setStep(3)}
                    className={`relative flex items-center gap-4 ${step === 3 ? "opacity-100" : "opacity-75"}`}
                  >
                    <div
                      className={`relative z-10 h-8 w-8 shrink-0 aspect-square rounded-full flex items-center justify-center font-bold text-xs transition-all ${step === 3
                        ? "bg-[#00966B] text-white ring-4 ring-emerald-100"
                        : "bg-slate-300 text-slate-600"
                        }`}
                    >
                      {isPriceComparison ? "2" : "3"}
                    </div>
                    <div>
                      <span className={`text-xs font-bold block ${step === 3 ? "text-[#00966B]" : "text-slate-500"}`}>
                        Supplier Accounts & Declaration
                      </span>
                    </div>
                  </div>
                </div>

                {/* NEED HELP CARD */}
                <div className="bg-[#E6F7F0] border border-[#C5EEDC] rounded-2xl p-3 flex items-center gap-2 shadow-xs">
                  <div className="text-[#00966B] flex items-center justify-center shrink-0 shadow-xs">
                    <Headphones className="h-3 w-3" />
                  </div>
                  <div className="text-xs">
                    <div className="font-bold text-[#0A2540]">Need help?</div>
                    <a href="mailto:info@cascadeplus.co.uk" className="text-[#00966B] font-semibold hover:underline">
                      info@cascadeplus.co.uk
                    </a>
                  </div>
                </div>

                {/* SMARTER ORDERING TOGETHER BRAND CARD */}
                <div className="rounded-2xl relative overflow-hidden shadow-xs h-50 border border-emerald-200/80 group">
                  <img
                    src="/assets/sidebar-pharmacy-background.png"
                    alt="Smarter Ordering Together"
                    className="w-full h-full object-cover"
                  />
                  {/* <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-emerald-800/40 to-emerald-500/20" /> */}
                  {/* <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                    <div className="text-xl font-bold tracking-tight leading-none italic font-serif text-emerald-100 drop-shadow-md">
                      Smarter
                    </div>
                    <div className="text-xl font-bold tracking-tight leading-none italic font-serif text-white drop-shadow-md flex items-center gap-1">
                      Ordering Together
                      <svg className="w-14 h-2 text-emerald-400 fill-current" viewBox="0 0 100 10">
                        <path d="M0 5 Q 50 0 100 8" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="lg:col-span-10 space-y-6">

              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* BANNER HEADER STEP 1 */}
                    <div className="bg-[#D7F4E6] border border-[#BAE8D3] rounded-3xl p-4 sm:p-5 flex items-center justify-between shadow-xs relative overflow-hidden min-h-[90px]">
                      <div className="max-w-lg space-y-0.5 relative z-10">
                        <h2 className="text-xl sm:text-2xl font-extrabold text-[#0A2540]">
                          Pharmacy Details
                        </h2>
                        <p className="text-xs text-slate-600 font-medium">
                          Let's start with some basic information about your pharmacy.
                        </p>
                      </div>
                      <div className="hidden sm:block absolute top-0 right-0 bottom-0 w-2/5 overflow-hidden rounded-r-3xl">
                        <img
                          src="/assets/pharmacy-details-storefront-banner.png"
                          alt="Pharmacy Storefront"
                          className="w-full h-full object-contain object-right"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#D7F4E6] via-[#D7F4E6]/50 to-transparent" />
                      </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-4 items-start">

                      {/* LEFT COLUMN: BASIC INFORMATION & PMR SYSTEM */}
                      <div className="lg:col-span-7 space-y-4">

                        {/* BASIC INFORMATION CARD */}
                        <FormCard
                          icon={Building2}
                          title="Basic Information"
                          subtitle="Please provide your pharmacy details."
                        >
                          <div className="space-y-2">
                            <HorizontalField
                              id="companyName"
                              label="Company Name"
                              required
                              value={step1.companyName}
                              maxLength={50}
                              onChange={(v) => handleStep1Change("companyName", v)}
                              onBlur={() => handleStep1Blur("companyName")}
                              error={errors.companyName}
                              placeholder="Enter company name"
                            />

                            <HorizontalField
                              id="pharmacyName"
                              label="Pharmacy Name"
                              required
                              value={step1.pharmacyName}
                              maxLength={50}
                              onChange={(v) => handleStep1Change("pharmacyName", v)}
                              onBlur={() => handleStep1Blur("pharmacyName")}
                              error={errors.pharmacyName}
                              placeholder="Enter pharmacy name"
                              testId={CONTACT.inputPharmacy}
                            />

                            <HorizontalField
                              id="pharmacyAddress"
                              label="Pharmacy Address"
                              required
                              isTextArea
                              rows={3}
                              value={step1.pharmacyAddress}
                              maxLength={150}
                              onChange={(v) => handleStep1Change("pharmacyAddress", v)}
                              onBlur={() => handleStep1Blur("pharmacyAddress")}
                              error={errors.pharmacyAddress}
                              placeholder="Enter pharmacy address"
                              testId={CONTACT.inputAddress}
                            />

                            <HorizontalField
                              id="pharmacyPostcode"
                              label="Pharmacy Postcode"
                              required
                              value={step1.pharmacyPostcode}
                              maxLength={10}
                              onChange={(v) => handleStep1Change("pharmacyPostcode", v)}
                              onBlur={() => handleStep1Blur("pharmacyPostcode")}
                              error={errors.pharmacyPostcode}
                              placeholder="e.g. SW1A 1AA"
                            />

                            <HorizontalField
                              id="telephoneNumber"
                              label="Telephone Number"
                              required
                              type="tel"
                              value={step1.telephoneNumber}
                              maxLength={20}
                              onChange={(v) => handleStep1Change("telephoneNumber", v.replace(/[^0-9\s\+\-\(\)]/g, ""))}
                              onBlur={() => handleStep1Blur("telephoneNumber")}
                              error={errors.telephoneNumber}
                              placeholder="e.g. 020 7123 4567"
                              testId={CONTACT.inputPhone}
                            />

                            <HorizontalField
                              id="emailAddress"
                              label="Email Address"
                              required
                              type="email"
                              value={step1.emailAddress}
                              maxLength={100}
                              onChange={(v) => handleStep1Change("emailAddress", v)}
                              onBlur={() => handleStep1Blur("emailAddress")}
                              error={errors.emailAddress}
                              placeholder="e.g. info@pharmacy.co.uk"
                              testId={CONTACT.inputEmail}
                            />

                            <HorizontalField
                              id="contactName"
                              label="Contact Name"
                              required
                              value={step1.contactName}
                              maxLength={50}
                              onChange={(v) => handleStep1Change("contactName", v.replace(/[^a-zA-Z\s'-]/g, ""))}
                              onBlur={() => handleStep1Blur("contactName")}
                              error={errors.contactName}
                              placeholder="Enter contact name"
                              testId={CONTACT.inputName}
                            />
                          </div>
                        </FormCard>

                        {/* PHARMACY PMR SYSTEM CARD */}
                        <FormCard
                          icon={Monitor}
                          title="Pharmacy PMR System"
                          subtitle="Please select your PMR system (tick one)."
                          required
                        >
                          <div className="grid sm:grid-cols-2 gap-2.5">
                            {["Proscript", "Apotec", "Rx Web", "Positive Solutions Analyst"].map((pmr) => (
                              <CustomRadioPill
                                key={pmr}
                                label={pmr}
                                checked={step1.pmrSystem === pmr}
                                onChange={() => handleStep1Change("pmrSystem", pmr)}
                              />
                            ))}
                          </div>
                        </FormCard>

                      </div>

                      {/* RIGHT COLUMN: SETUP INFO, TRANSMISSION TYPE, PHARMACY GROUP */}
                      <div className="lg:col-span-5 space-y-4">

                        {/* SETUP INFORMATION CARD */}
                        <FormCard
                          icon={SlidersHorizontal}
                          title="Setup Information"
                          subtitle="Tell us how you would like to set up Cascade Plus."
                        >
                          <div>
                            <label className="block text-xs font-bold text-[#0A2540] mb-2">
                              Setup Type <span className="text-rose-500">*</span>
                            </label>
                            <div className="space-y-2">
                              {["Price Comparison", "Preferred Supplier"].map((type) => (
                                <CustomRadioPill
                                  key={type}
                                  label={type}
                                  checked={step1.setupType === type}
                                  onChange={() => handleStep1Change("setupType", type)}
                                />
                              ))}
                            </div>
                          </div>
                        </FormCard>

                        {/* TRANSMISSION TYPE CARD */}
                        <FormCard
                          icon={Package}
                          title="Transmission Type"
                          subtitle="Tell us how you would like to order with Cascade Plus."
                          required
                        >
                          <div className="space-y-2">
                            {["Orderpad", "PMR to Orderpad", "PMR to Order History"].map((mode) => (
                              <CustomRadioPill
                                key={mode}
                                label={mode}
                                checked={step1.transmissionType === mode}
                                onChange={() => handleStep1Change("transmissionType", mode)}
                              />
                            ))}
                          </div>
                        </FormCard>

                        {/* PHARMACY GROUP CARD */}
                        <FormCard
                          icon={Users}
                          title="Pharmacy Group"
                          subtitle="Let us know if you are part of a pharmacy group."
                        >
                          <div>
                            <label className="block text-xs font-bold text-[#0A2540] mb-2">
                              Part of a Pharmacy Group? <span className="text-rose-500">*</span>
                            </label>
                            <div className="flex items-center gap-3">
                              {["Yes", "No"].map((choice) => (
                                <CustomRadioPill
                                  key={choice}
                                  label={choice}
                                  checked={step1.partOfGroup === choice}
                                  onChange={() => handleStep1Change("partOfGroup", choice)}
                                />
                              ))}
                            </div>
                          </div>

                          {step1.partOfGroup === "Yes" && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="pt-3 border-t border-slate-100 space-y-4"
                            >
                              <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-bold text-[#0A2540] mb-1.5">
                                    Number in Pharmacy Group
                                  </label>
                                  <input
                                    type="text"
                                    value={step1.groupNumber}
                                    maxLength={10}
                                    onChange={(e) => handleStep1Change("groupNumber", e.target.value.replace(/[^0-9]/g, ""))}
                                    placeholder="Enter number"
                                    className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${errors.groupNumber
                                      ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                                      : "border-slate-200 focus:ring-[#00966B]"
                                      }`}
                                  />
                                  {errors.groupNumber && (
                                    <p className="mt-0.5 text-[11px] text-rose-600 font-medium">{errors.groupNumber}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-xs font-bold text-[#0A2540] mb-1.5">
                                    Pharmacy Group Name
                                  </label>
                                  <input
                                    type="text"
                                    value={step1.groupName}
                                    maxLength={50}
                                    onChange={(e) => handleStep1Change("groupName", e.target.value)}
                                    placeholder="Enter group name"
                                    className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${errors.groupName
                                      ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                                      : "border-slate-200 focus:ring-[#00966B]"
                                      }`}
                                  />
                                  {errors.groupName && (
                                    <p className="mt-0.5 text-[11px] text-rose-600 font-medium">{errors.groupName}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-xs font-bold text-[#0A2540] mb-1.5">
                                    Group Accounts
                                  </label>
                                  <input
                                    type="text"
                                    value={step1.groupAccounts}
                                    maxLength={50}
                                    onChange={(e) => handleStep1Change("groupAccounts", e.target.value)}
                                    placeholder="Enter group accounts"
                                    className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${errors.groupAccounts
                                      ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                                      : "border-slate-200 focus:ring-[#00966B]"
                                      }`}
                                  />
                                  {errors.groupAccounts && (
                                    <p className="mt-0.5 text-[11px] text-rose-600 font-medium">{errors.groupAccounts}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-xs font-bold text-[#0A2540] mb-1.5">
                                    Buying Group
                                  </label>
                                  <input
                                    type="text"
                                    value={step1.buyingGroup}
                                    maxLength={50}
                                    onChange={(e) => handleStep1Change("buyingGroup", e.target.value)}
                                    placeholder="Enter buying group"
                                    className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${errors.buyingGroup
                                      ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                                      : "border-slate-200 focus:ring-[#00966B]"
                                      }`}
                                  />
                                  {errors.buyingGroup && (
                                    <p className="mt-0.5 text-[11px] text-rose-600 font-medium">{errors.buyingGroup}</p>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </FormCard>

                      </div>

                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* BANNER HEADER STEP 2 */}
                    <div className="bg-[#D7F4E6] border border-[#BAE8D3] rounded-3xl p-4 sm:p-5 flex items-center justify-between shadow-xs relative overflow-hidden min-h-[90px]">
                      <img class="absolute inset-y-0 right-0 hidden h-full w-[285px] object-cover object-left sm:block" src="../assets/ordering-preferences-pills-banner.png" alt="" />
                      <div class="absolute inset-y-0 right-[205px] hidden w-24 bg-gradient-to-r from-[#dff6ed] to-transparent sm:block"></div>
                      <div className="max-w-lg space-y-0.5 relative z-10">
                        <h2 className="text-xl sm:text-2xl font-extrabold text-[#0A2540]">
                          Ordering Preferences
                        </h2>
                        <p className="text-xs text-slate-600 font-medium">
                          Set the supplier order you would like Cascade Plus to use.
                        </p>
                      </div>
                      {/* <PillBannerIllustration /> */}

                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {/* GENERICS SUPPLIER PRIORITY */}
                      <FormCard
                        icon={Package}
                        title="Generics Supplier Priority"
                        subtitle="Add wholesalers in the order you would like us to prioritise for generic medicines."
                      >
                        {errors.generics && (
                          <p className="text-xs text-rose-600 font-medium">{errors.generics}</p>
                        )}

                        <div className="space-y-3">
                          {genericsWholesalers.map((wholesaler, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="h-7 w-7 rounded-full bg-[#0A2540] text-white font-bold text-xs flex items-center justify-center shrink-0">
                                {idx + 1}
                              </div>
                              <div className="relative flex-1">
                                <select
                                  id={`genericSuppliers-${idx}`}
                                  value={wholesaler}
                                  onChange={(e) => {
                                    const updated = [...genericsWholesalers];
                                    updated[idx] = e.target.value;
                                    setGenericsWholesalers(updated);
                                  }}
                                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 pr-9 text-xs sm:text-sm text-[#0A2540] outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                                >
                                  <option value="">Select wholesaler</option>
                                  {WHOLESALER_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                              </div>
                              {genericsWholesalers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setGenericsWholesalers(genericsWholesalers.filter((_, i) => i !== idx))
                                  }
                                  className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const unused = WHOLESALER_OPTIONS.find((w) => !genericsWholesalers.includes(w)) || "Sigma Pharmaceuticals";
                            setGenericsWholesalers([...genericsWholesalers, unused]);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00966B] hover:text-[#007F5A] pt-2"
                        >
                          <Plus className="h-4 w-4" /> Add another wholesaler
                        </button>
                      </FormCard>

                      {/* PI SUPPLIER PRIORITY */}
                      <FormCard
                        icon={Package}
                        title="PI Supplier Priority"
                        subtitle="Add wholesalers in the order you would like us to prioritise for parallel imports."
                      >
                        {errors.pi && (
                          <p className="text-xs text-rose-600 font-medium">{errors.pi}</p>
                        )}

                        <div className="space-y-3">
                          {piWholesalers.map((wholesaler, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="h-7 w-7 rounded-full bg-[#0A2540] text-white font-bold text-xs flex items-center justify-center shrink-0">
                                {idx + 1}
                              </div>
                              <div className="relative flex-1">
                                <select
                                  id={`piSuppliers-${idx}`}
                                  value={wholesaler}
                                  onChange={(e) => {
                                    const updated = [...piWholesalers];
                                    updated[idx] = e.target.value;
                                    setPiWholesalers(updated);
                                  }}
                                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 pr-9 text-xs sm:text-sm text-[#0A2540] outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                                >
                                  <option value="">Select wholesaler</option>
                                  {WHOLESALER_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                              </div>
                              {piWholesalers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPiWholesalers(piWholesalers.filter((_, i) => i !== idx))
                                  }
                                  className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const unused = WHOLESALER_OPTIONS.find((w) => !piWholesalers.includes(w)) || "AAH Pharmaceuticals";
                            setPiWholesalers([...piWholesalers, unused]);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00966B] hover:text-[#007F5A] pt-2"
                        >
                          <Plus className="h-4 w-4" /> Add another wholesaler
                        </button>
                      </FormCard>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* BANNER HEADER STEP 3 */}
                    <div className="bg-[#D7F4E6] border border-[#BAE8D3] rounded-3xl p-4 sm:p-5 flex items-center justify-between shadow-xs relative overflow-hidden min-h-[90px]">
                      <div className="max-w-lg space-y-0.5 relative z-10">
                        <h2 className="text-xl sm:text-2xl font-extrabold text-[#0A2540]">
                          Supplier Accounts & Declaration
                        </h2>
                        <p className="text-xs text-slate-600 font-medium">
                          Add your account details and confirm your authority to submit this request.
                        </p>
                      </div>
                      {/* <ContractBannerIllustration /> */}
                      <img className="absolute inset-y-0 right-0 hidden h-full w-[285px] object-cover object-left sm:block" src="../assets/supplier-declaration-clipboard-banner.png" alt="" />
                      <div className="absolute inset-y-0 right-[205px] hidden w-24 bg-gradient-to-r from-[#dff6ed] to-transparent sm:block"></div>
                    </div>

                    {/* WHOLESALER ACCOUNT DETAILS CARD */}
                    <FormCard
                      icon={Building2}
                      title="Wholesaler Account Details"
                      subtitle="Add the supplier accounts you would like connected to Cascade Plus."
                    >
                      <div className="space-y-3">
                        {wholesalerAccounts.map((acc, idx) => (
                          <div key={idx} className="grid sm:grid-cols-2 gap-3 items-start">
                            <div>
                              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                Supplier <span className="text-rose-500">*</span>
                              </label>
                              <div className="relative">
                                <select
                                  id={`wholesalerSupplier-${idx}`}
                                  value={acc.supplier}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const updated = [...wholesalerAccounts];
                                    updated[idx].supplier = val;
                                    setWholesalerAccounts(updated);
                                    setErrors((prev) => {
                                      const copy = { ...prev };
                                      if (val) delete copy[`acc_supplier_${idx}`];
                                      return copy;
                                    });
                                  }}
                                  className={`w-full appearance-none rounded-xl border bg-white px-3.5 py-2.5 pr-9 text-xs sm:text-sm text-[#0A2540] outline-none transition-all ${errors[`acc_supplier_${idx}`]
                                    ? "border-rose-300 focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
                                    : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                                    }`}
                                >
                                  <option value="">Select wholesaler</option>
                                  {WHOLESALER_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                              </div>
                              {errors[`acc_supplier_${idx}`] && (
                                <p className="mt-0.5 text-[11px] text-rose-600 font-medium">
                                  {errors[`acc_supplier_${idx}`]}
                                </p>
                              )}
                            </div>

                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                  Account Number <span className="text-rose-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={acc.accountNumber}
                                  maxLength={12}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const updated = [...wholesalerAccounts];
                                    let finalVal = val;
                                    let err = undefined;

                                    if (val.length >= 12) {
                                      finalVal = val.slice(0, 12);
                                      err = "Maximum length exceeded.";
                                    }

                                    updated[idx].accountNumber = finalVal;
                                    setWholesalerAccounts(updated);

                                    setErrors((prev) => {
                                      const copy = { ...prev };
                                      const errKey = `acc_number_${idx}`;
                                      if (err) {
                                        copy[errKey] = err;
                                      } else {
                                        delete copy[errKey];
                                      }
                                      return copy;
                                    });
                                  }}
                                  placeholder="Enter account number"
                                  className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] focus:outline-none focus:ring-2 transition-all ${errors[`acc_number_${idx}`]
                                    ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                                    : "border-slate-200 focus:ring-[#00966B]"
                                    }`}
                                />
                                {errors[`acc_number_${idx}`] && (
                                  <p className="mt-0.5 text-[11px] text-rose-600 font-medium">
                                    {errors[`acc_number_${idx}`]}
                                  </p>
                                )}
                              </div>
                              {wholesalerAccounts.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setWholesalerAccounts(
                                      wholesalerAccounts.filter((_, i) => i !== idx)
                                    );
                                    setErrors((prev) => {
                                      const copy = { ...prev };
                                      delete copy[`acc_supplier_${idx}`];
                                      delete copy[`acc_number_${idx}`];
                                      return copy;
                                    });
                                  }}
                                  className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg transition-colors mt-5"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setWholesalerAccounts([
                            ...wholesalerAccounts,
                            { supplier: "", accountNumber: "" },
                          ])
                        }
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00966B] hover:text-[#007F5A] pt-2"
                      >
                        <Plus className="h-4 w-4" /> Add another supplier
                      </button>
                    </FormCard>

                    {/* DECLARATION CARD */}
                    <FormCard
                      icon={ShieldCheck}
                      title="Declaration"
                      subtitle="Please read and accept the declaration before submitting."
                    >
                      {/* LEGAL DECLARATION BOX */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed max-h-36 overflow-y-auto">
                        I confirm that I am authorised on behalf of the pharmacy business named on the first page to sign up the pharmacy/pharmacies. I, below named Authorised Representative, in my capacity as an authorised representative of the above-named Pharmacy, hereby confirm that the pharmacy instructs the Luxsh Technologies Ltd UK support team*, for the above named site(s), to configure the software via remote support, and to allow the Cascade Plus to provide the service as detailed in the agreement. Fully IG Accredited. IG Organisation Code: H1Q5J
                      </div>

                      {/* CHECKBOX */}
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={declarationAgreed}
                          onChange={(e) => {
                            setDeclarationAgreed(e.target.checked);
                            if (errors.declaration) {
                              setErrors((prev) => ({ ...prev, declaration: undefined }));
                            }
                          }}
                          className="mt-0.5 h-4 w-4 rounded text-[#00966B] focus:ring-[#00966B]"
                        />
                        <span className="text-xs sm:text-sm font-semibold text-[#0A2540]">
                          I agree to the above declaration <span className="text-rose-500">*</span>
                        </span>
                      </label>
                      {errors.declaration && (
                        <p className="text-xs text-rose-600 font-medium">{errors.declaration}</p>
                      )}

                      {/* PRIVACY / SECURITY BANNER */}
                      <div className="bg-[#E6F7F0] border border-[#C5EEDC] rounded-xl px-4 py-2.5 flex items-center justify-between text-xs text-[#00966B]">
                        <div className="flex items-center gap-2">
                          <Lock className="h-3.5 w-3.5" />
                          <span>Your information is securely used to configure your Cascade Plus account.</span>
                        </div>
                        <Link to="/privacy-policy" className="font-semibold underline hover:text-[#007F5A]">
                          Privacy Policy
                        </Link>
                      </div>

                      {/* FORM INPUTS GRID */}
                      <div className="grid sm:grid-cols-3 gap-4">
                        <InputField
                          label="Date"
                          required
                          type="date"
                          max={todayStr}
                          value={declarationDate}
                          onKeyDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            try {
                              e.target.showPicker?.();
                            } catch (err) { }
                          }}
                          onChange={(v) => {
                            setDeclarationDate(v);
                            if (errors.declarationDate) setErrors((prev) => ({ ...prev, declarationDate: undefined }));
                          }}
                          error={errors.declarationDate}
                        />

                        <InputField
                          label="Name"
                          required
                          value={repName}
                          maxLength={50}
                          onChange={(v) => {
                            const cleaned = v.replace(/[^a-zA-Z\s'-]/g, "");
                            if (cleaned.length >= 50) {
                              setRepName(cleaned.slice(0, 50));
                              setErrors((prev) => ({ ...prev, repName: "Maximum length exceeded." }));
                            } else {
                              setRepName(cleaned);
                              setErrors((prev) => {
                                const copy = { ...prev };
                                if (copy.repName === "Maximum length exceeded." || copy.repName === "Required") delete copy.repName;
                                return copy;
                              });
                            }
                          }}
                          error={errors.repName}
                          placeholder="Enter full name"
                        />

                        <InputField
                          label="Position"
                          required
                          value={repPosition}
                          maxLength={50}
                          onChange={(v) => {
                            const cleaned = v.replace(/[^a-zA-Z\s'-]/g, "");
                            if (cleaned.length >= 50) {
                              setRepPosition(cleaned.slice(0, 50));
                              setErrors((prev) => ({ ...prev, repPosition: "Maximum length exceeded." }));
                            } else {
                              setRepPosition(cleaned);
                              setErrors((prev) => {
                                const copy = { ...prev };
                                if (copy.repPosition === "Maximum length exceeded." || copy.repPosition === "Required") delete copy.repPosition;
                                return copy;
                              });
                            }
                          }}
                          error={errors.repPosition}
                          placeholder="Enter position"
                        />
                      </div>

                      {/* SIGNATURE DRAWING CANVAS */}
                      <SignatureCanvas
                        onSignChange={(data) => {
                          setSignatureData(data);
                          if (errors.signature) setErrors((prev) => ({ ...prev, signature: undefined }));
                        }}
                        onClear={() => setSignatureData(null)}
                      />
                      {errors.signature && (
                        <p className="text-xs text-rose-600 font-medium">{errors.signature}</p>
                      )}
                    </FormCard>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* NAVIGATION / SUBMIT FOOTER */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-200/80">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="inline-flex items-center gap-2 bg-[#E8F3EE] hover:bg-slate-200 text-slate-600 px-6 py-2.5 rounded-full font-semibold transition-all text-xs sm:text-sm shadow-xs border border-slate-200"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center gap-2 bg-[#E8F3EE] text-slate-400 px-6 py-2.5 rounded-full font-semibold text-xs sm:text-sm border border-slate-200 cursor-not-allowed"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center gap-2 bg-[#007F5A] hover:bg-[#00684A] text-white px-7 py-2.5 rounded-full font-bold transition-all text-xs sm:text-sm shadow-sm"
                  >
                    Save & Continue <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid={CONTACT.submitButton}
                    className="inline-flex items-center gap-2 bg-[#007F5A] hover:bg-[#00684A] text-white px-8 py-3 rounded-full font-bold transition-all text-xs sm:text-sm shadow-sm disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : "Submit Request"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400 pt-1">
                <Lock className="h-3.5 w-3.5" />
                <span>Your information is secure and will only be used to set up your Cascade Plus account.</span>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}

/* HELPER COMPONENTS */
function FormCard({ icon: Icon, title, subtitle, required, children }) {
  return (
    <div className="bg-white rounded-2xl border border-emerald-100/90 overflow-hidden shadow-xs">
      <div className="flex items-center gap-3 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 via-emerald-50/55 to-white px-4 py-2.5">
        <div className="h-7.5 w-7.5 rounded-full bg-emerald-100/70 text-emerald-600 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-xs sm:text-sm font-bold text-[#0A2540]">
            {title} {required && <span className="text-rose-500">*</span>}
          </h3>
          <p className="text-[10px] sm:text-[11px] text-slate-500 leading-tight">{subtitle}</p>
        </div>
      </div>
      <div className="p-3.5 sm:p-4 bg-white space-y-2.5">
        {children}
      </div>
    </div>
  );
}

function HorizontalField({ id, label, required, value, onChange, onBlur, error, placeholder, type = "text", testId, isTextArea, rows = 2, maxLength }) {
  const fieldId = id || (label ? `field-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}` : undefined);
  const errorId = fieldId ? `${fieldId}-error` : undefined;

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-[150px_1fr] ${isTextArea ? "items-start" : "items-center"} gap-1.5 sm:gap-3`}>
      <label htmlFor={fieldId} className={`block text-xs font-bold text-[#0A2540] ${isTextArea ? "sm:pt-1.5" : ""}`}>
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div>
        {isTextArea ? (
          <textarea
            id={fieldId}
            rows={rows}
            value={value}
            maxLength={maxLength}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            data-testid={testId}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error && errorId ? errorId : undefined}
            className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all resize-none ${error
              ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
              : "border-slate-200/90 focus:ring-[#00966B] focus:border-[#00966B]"
              }`}
          />
        ) : (
          <input
            id={fieldId}
            type={type}
            value={value}
            maxLength={maxLength}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            data-testid={testId}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error && errorId ? errorId : undefined}
            className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${error
              ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
              : "border-slate-200/90 focus:ring-[#00966B] focus:border-[#00966B]"
              }`}
          />
        )}
        {error && <p id={errorId} className="mt-0.5 text-[11px] text-rose-600 font-medium">{error}</p>}
      </div>
    </div>
  );
}

function InputField({ label, required, value, onChange, error, placeholder, type = "text", testId, max, maxLength, onKeyDown, onClick }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#0A2540] mb-0.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        max={max}
        maxLength={maxLength}
        onKeyDown={onKeyDown}
        onClick={onClick}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm text-[#0A2540] bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${error
          ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
          : "border-slate-200/90 focus:ring-[#00966B] focus:border-[#00966B]"
          }`}
      />
      {error && <p className="mt-0.5 text-[11px] text-rose-600 font-medium">{error}</p>}
    </div>
  );
}

function CustomRadioPill({ label, checked, onChange }) {
  return (
    <div
      onClick={onChange}
      className={`flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border transition-all cursor-pointer ${checked
        ? "border-[#00966B] bg-[#E6F7F0] text-[#0A2540] font-semibold shadow-xs"
        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        }`}
    >
      <div
        className={`h-3.5 w-3.5 rounded-full flex items-center justify-center shrink-0 transition-all ${checked
          ? "border-4 border-[#00966B] bg-white"
          : "border-2 border-slate-300 bg-white"
          }`}
      />
      <span className="text-xs sm:text-sm">{label}</span>
    </div>
  );
}
{/*
function PillBannerIllustration() {
  return (
    <div className="hidden sm:flex relative w-44 h-20 items-center justify-center shrink-0">
       <svg viewBox="0 0 160 80" className="w-full h-full object-contain">
        <rect x="20" y="25" width="60" height="30" rx="15" fill="#00966B" transform="rotate(-15 50 40)" />
        <rect x="50" y="25" width="30" height="30" rx="0" fill="#FFFFFF" transform="rotate(-15 50 40)" />
        <rect x="80" y="30" width="60" height="30" rx="15" fill="#3B82F6" transform="rotate(10 110 45)" />
        <rect x="110" y="30" width="30" height="30" rx="0" fill="#FFFFFF" transform="rotate(10 110 45)" />
      </svg> 
      <img class="absolute inset-y-0 right-0 hidden h-full w-[285px] object-cover object-left sm:block" src="../assets/ordering-preferences-pills-banner.png" alt="" />
    </div>
  );
}*/}

function ContractBannerIllustration() {
  return (
    <div className="hidden sm:flex relative w-44 h-20 items-center justify-center shrink-0">
      <svg viewBox="0 0 160 80" className="w-full h-full object-contain">
        <rect x="30" y="10" width="70" height="65" rx="4" fill="#FFFFFF" stroke="#D1EBE0" strokeWidth="2" />
        <rect x="40" y="20" width="50" height="6" rx="2" fill="#00966B" />
        <rect x="40" y="32" width="40" height="4" rx="2" fill="#CBD5E1" />
        <rect x="40" y="42" width="45" height="4" rx="2" fill="#CBD5E1" />
        <path d="M40 56 Q55 50 70 56 T90 54" stroke="#00966B" strokeWidth="2" fill="none" />
        <circle cx="115" cy="40" r="18" fill="#00966B" opacity="0.1" />
        <path d="M107 40 L113 46 L123 34" stroke="#00966B" strokeWidth="3" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function SignatureCanvas({ onSignChange, onClear }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A2540";
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSigned(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas && onSignChange) {
      onSignChange(canvas.toDataURL());
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
    if (onClear) onClear();
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-bold text-[#0A2540]">
          Signature <span className="text-rose-500">*</span>
        </label>
        <button
          type="button"
          onClick={clearCanvas}
          className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 font-medium underline"
        >
          <RotateCcw className="h-3 w-3" /> Clear
        </button>
      </div>
      <div className="relative border border-slate-200 rounded-xl bg-slate-50/50 overflow-hidden">
        <canvas
          ref={canvasRef}
          width={600}
          height={140}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className="w-full h-36 touch-none cursor-crosshair bg-white"
        />
        {!hasSigned && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-xs sm:text-sm italic">
            Sign above using your mouse, trackpad or finger
          </div>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Your drawn signature will be included with this request.
      </p>
    </div>
  );
}
